import { useEffect, useState } from 'react';
import { api } from '../api';
import type { OficioOnde } from '../api/types';
import { t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { resumoBonus } from './casa/CasaUi';

/**
 * A faixa no alto das janelas de ofício (techspec-marceneiro-e-oficios-em-casa §05.4): o bônus da casa quando vale,
 * ou por que não craftar aqui. Quem decide é o servidor (`GET api/oficio/{oficio}/onde`); a faixa só mostra, e o
 * craft continua conferido lá (409 `oficio.semFerramentaPerto`).
 */
export function FaixaOficio({ oficio, active, tick }: { oficio: string; active: boolean; tick: number }) {
  const [onde, setOnde] = useState<OficioOnde | null>(null);
  useEffect(() => {
    if (!active) return;
    let live = true;
    const ler = () => api.oficioOnde(oficio).then(o => { if (live) setOnde(o); }).catch(() => { if (live) setOnde(null); });
    void ler();
    // O personagem anda com a janela aberta: relê de tempos em tempos (perto/longe da ferramenta).
    const id = window.setInterval(() => { if (!document.hidden) void ler(); }, 4000);
    return () => { live = false; window.clearInterval(id); };
  }, [active, tick, oficio]);

  if (!onde) return null;
  if (onde.bonus)
    return (
      <p className="oficio-faixa is-bonus" role="status">
        <b>✦ {t('oficio.casa.bonus')}</b> <span>{resumoBonus(onde.bonus)}</span>
      </p>
    );
  if (!onde.perto && onde.fx != null && onde.defId)
    return (
      <p className="oficio-faixa" role="status">
        {t('oficio.vaiAte', { nome: nomeDoItem(onde.defId), n: onde.distancia ?? 0 })}
      </p>
    );
  if (!onde.perto)
    return (
      <p className="oficio-faixa is-trava" role="alert">
        🔒 {t(onde.motivo ?? 'oficio.semFerramentaPerto', { n: onde.distanciaMax })}
      </p>
    );
  if (onde.onde === 'casa' && onde.motivo)
    return <p className="oficio-faixa" role="status">{t(onde.motivo, { n: onde.distanciaMax })}</p>;
  return null;
}

/* ================================================================ Ir até a ferramenta (Renato, 03/11/2026)
   Na cidade, ao craftar longe: o personagem anda até a ferramenta mais perto e crafta ao chegar. Na casa não anda: basta
   a casa ter a ferramenta (Renato, 04/11/2026), e o servidor já responde `perto`.
   A tela do mundo registra como andar (`definirIrAte`); as oficinas chamam `garantirPerto` antes de pedir ao servidor. */
type IrAte = (x: number, y: number, raio: number) => Promise<boolean>;
let irAte: IrAte | null = null;

export function definirIrAte(fn: IrAte | null): void {
  irAte = fn;
}

/**
 * Perto da ferramenta do ofício? Se não, anda até ela e devolve quando chegar. `false` com o motivo quando não dá
 * (cidade sem ferramenta, fora do mundo, caminho bloqueado). Quem confere de verdade, na hora de fazer, é o servidor.
 */
export async function garantirPerto(oficio: string): Promise<{ ok: boolean; motivo?: string }> {
  let onde: OficioOnde;
  try {
    onde = await api.oficioOnde(oficio);
  } catch {
    return { ok: true }; // sem resposta, o servidor decide no fazer
  }
  if (onde.perto) return { ok: true };
  if (onde.fx == null || onde.fy == null || !irAte)
    return { ok: false, motivo: t(onde.motivo ?? 'oficio.semFerramentaPerto', { n: onde.distanciaMax }) };
  // Encosta na ferramenta (1 passo), para não depender do servidor já ter o último passo.
  const chegou = await irAte(onde.fx, onde.fy, 1);
  if (!chegou) return { ok: false, motivo: t('oficio.caminhoBloqueado') };
  // A posição que vale é a do servidor: espera ele confirmar que está perto antes do som e da primeira peça.
  for (let i = 0; i < 12; i++) {
    try {
      const agora = await api.oficioOnde(oficio);
      if (agora.perto) return { ok: true };
    } catch { return { ok: true }; }
    await new Promise(ok => window.setTimeout(ok, 350));
  }
  return { ok: false, motivo: t('oficio.semFerramentaPerto', { n: onde.distanciaMax }) };
}
